/**
 * Time Input Components
 * Handles rendering of time inputs for both 12hr and 24hr formats
 */

import React from "react";
import TimestampConverter from "../helpers/TimestampConverter";
import { convertTo24HourFormatback } from "../services/timeFormat";
import { getTimeInputErrorStyle } from "../utils/constants";
import { CSS_CLASSES, COLORS, DEFAULT_VALUES, INLINE_STYLES } from "../utils/cardBodyConstants";

/**
 * Time Inputs Component for Edit Mode
 */
export const EditTimeInputs = ({
  timeFormat,
  startTimeEdit,
  endTimeEdit,
  startTimeEdit24,
  endTimeEdit24,
  uStartTimeError,
  uEndTimeError,
  editedId,
  groupId,
  startInputRef,
  endInputRef,
  start12TimeInputRef,
  end12TimeInputRef,
  onChangeStartTime,
  onChangeEndTime,
  onBlurStart,
  onBlurEnd,
  onFocus,
  validateNoAlphabets,
}) => {
  if (timeFormat === 24) {
    return (
      <>
        <input
          ref={startInputRef}
          type="text"
          name="start_time"
          value={startTimeEdit24}
          style={uStartTimeError ? { backgroundColor: COLORS.ERROR_BACKGROUND } : {}}
          onChange={(e) => {
            if (validateNoAlphabets(e.target.value)) {
              onChangeStartTime(e.target.value);
            }
          }}
          onBlur={onBlurStart}
          onFocus={onFocus}
          className={`${editedId === groupId ? `${CSS_CLASSES.EDIT_TIME} ${CSS_CLASSES.EDIT_TIME_START}` : ""}`}
          autoComplete="off"
        />
        -
        <input
          ref={endInputRef}
          type="text"
          name="end_time"
          value={endTimeEdit24}
          style={uEndTimeError ? { backgroundColor: COLORS.ERROR_BACKGROUND } : {}}
          onChange={(e) => {
            if (validateNoAlphabets(e.target.value)) {
              onChangeEndTime(e.target.value);
            }
          }}
          onBlur={onBlurEnd}
          onFocus={onFocus}
          className={`${editedId === groupId ? `${CSS_CLASSES.EDIT_TIME} ${CSS_CLASSES.EDIT_TIME_END}` : ""}`}
          autoComplete="off"
        />
      </>
    );
  }

  return (
    <>
      <input
        ref={start12TimeInputRef}
        type="text"
        name="start_time"
        value={startTimeEdit}
        style={uStartTimeError ? { backgroundColor: COLORS.ERROR_BACKGROUND } : {}}
        onChange={(e) => onChangeStartTime(e.target.value)}
        onBlur={onBlurStart}
        onFocus={onFocus}
        className={`${editedId === groupId ? `${CSS_CLASSES.EDIT_TIME} ${CSS_CLASSES.EDIT_TIME_START}` : ""}`}
        autoComplete="off"
      />
      -
      <input
        ref={end12TimeInputRef}
        type="text"
        name="end_time"
        value={endTimeEdit}
        style={uEndTimeError ? { backgroundColor: COLORS.ERROR_BACKGROUND } : {}}
        onChange={(e) => onChangeEndTime(e.target.value)}
        onBlur={onBlurEnd}
        onFocus={onFocus}
        className={`${editedId === groupId ? `${CSS_CLASSES.EDIT_TIME} ${CSS_CLASSES.EDIT_TIME_END}` : ""}`}
        autoComplete="off"
      />
    </>
  );
};

/**
 * Time Inputs Component for Display Mode (Read-only)
 */
export const DisplayTimeInputs = ({
  timeFormat,
  startTime,
  endTime,
  zone,
  onClick,
}) => {
  if (timeFormat === 24) {
    return (
      <>
        <input
          style={INLINE_STYLES.CURSOR_POINTER}
          type="text"
          name="start_time"
          autoComplete="off"
          readOnly
          value={startTime ? convertTo24HourFormatback(TimestampConverter(startTime, zone)) || '' : ''}
          className={`${CSS_CLASSES.TIME_SHOW} ${CSS_CLASSES.EDIT_TIME_START}`}
        />
        -
        <input
          style={INLINE_STYLES.CURSOR_POINTER}
          type="text"
          name="end_time"
          autoComplete="off"
          readOnly
          value={endTime ? convertTo24HourFormatback(TimestampConverter(endTime, zone)) || '' : ''}
          className={`${CSS_CLASSES.TIME_SHOW} ${CSS_CLASSES.EDIT_TIME_END}`}
        />
      </>
    );
  }

  return (
    <>
      <input
        style={INLINE_STYLES.CURSOR_POINTER}
        type="text"
        name="start_time"
        autoComplete="off"
        readOnly
        value={startTime ? TimestampConverter(startTime, zone) || '' : ''}
        className={`${CSS_CLASSES.TIME_SHOW} ${CSS_CLASSES.EDIT_TIME_START}`}
      />
      -
      <input
        style={INLINE_STYLES.CURSOR_POINTER}
        type="text"
        name="end_time"
        autoComplete="off"
        readOnly
        value={endTime ? TimestampConverter(endTime, zone) || '' : ''}
        className={`${CSS_CLASSES.TIME_SHOW} ${CSS_CLASSES.EDIT_TIME_END}`}
      />
    </>
  );
};

/**
 * Wrapper component for EditTimeInputs with time-overview-sec div
 */
export const EditTimeInputsWrapper = ({
  timeFormat,
  startTimeEdit,
  endTimeEdit,
  startTimeEdit24,
  endTimeEdit24,
  uStartTimeError,
  uEndTimeError,
  editedId,
  groupId,
  startInputRef,
  endInputRef,
  start12TimeInputRef,
  end12TimeInputRef,
  onChangeStartTime24,
  onChangeEndTime24,
  onChangeStartTime,
  onChangeEndTime,
  onBlurStart,
  onBlurEnd,
  onFocus,
  validateNoAlphabets,
  onClick,
  className = "",
}) => {
  return (
    <div className={`${CSS_CLASSES.TIME_OVERVIEW_SEC} ${className}`} onClick={onClick}>
      <EditTimeInputs
        timeFormat={timeFormat}
        startTimeEdit={startTimeEdit}
        endTimeEdit={endTimeEdit}
        startTimeEdit24={startTimeEdit24}
        endTimeEdit24={endTimeEdit24}
        uStartTimeError={uStartTimeError}
        uEndTimeError={uEndTimeError}
        editedId={editedId}
        groupId={groupId}
        startInputRef={startInputRef}
        endInputRef={endInputRef}
        start12TimeInputRef={start12TimeInputRef}
        end12TimeInputRef={end12TimeInputRef}
        onChangeStartTime={timeFormat === 24 ? onChangeStartTime24 : onChangeStartTime}
        onChangeEndTime={timeFormat === 24 ? onChangeEndTime24 : onChangeEndTime}
        onBlurStart={onBlurStart}
        onBlurEnd={onBlurEnd}
        onFocus={onFocus}
        validateNoAlphabets={validateNoAlphabets}
      />
    </div>
  );
};

/**
 * Wrapper component for DisplayTimeInputs with time-overview-sec div
 */
export const DisplayTimeInputsWrapper = ({
  timeFormat,
  startTime,
  endTime,
  zone,
  onClick,
  className = "",
}) => {
  return (
    <div className={`${CSS_CLASSES.TIME_OVERVIEW_SEC} ${className}`} onClick={onClick}>
      <DisplayTimeInputs
        timeFormat={timeFormat}
        startTime={startTime}
        endTime={endTime}
        zone={zone}
        onClick={onClick}
      />
    </div>
  );
};
